import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Container,
  Heading,
  Input,
  Select,
  Table,
  TableRow,
  TableData,
  Error,
  LoadingContainer,
  LoadingSpinner,
  FlexContainer,
  ListContainer
} from '../../styled/common';

const Timeline = ({ user }) => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    tech: '',
    state: '',
    statusType: '',
    orderNumber: '',
    material: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get('/api/timeline');
        setLogs(response.data);
        setFilteredLogs(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching timeline:', err);
        setError('Failed to load timeline data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) fetchTimeline();
  }, [user]);

  useEffect(() => {
    const filtered = logs.filter((log) => {
      const matchesDate = !filters.date || log.date?.startsWith(filters.date);
      const matchesTech = !filters.tech || log.techName?.toLowerCase().includes(filters.tech.toLowerCase());
      const matchesState = !filters.state || log.state === filters.state;
      const matchesStatus = !filters.statusType || log.statusField === filters.statusType;
      const matchesOrder = !filters.orderNumber || log.orderNumber?.includes(filters.orderNumber);
      const matchesMaterial = !filters.material || log.material?.includes(filters.material);
      return matchesDate && matchesTech && matchesState && matchesStatus && matchesOrder && matchesMaterial;
    });
    setFilteredLogs(filtered);
  }, [filters, logs]);

  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDelete = async (log) => {
    const confirmed = window.confirm("Are you sure you want to delete this action?");
    if (!confirmed) return;
    try {
      await axios.delete('/api/salesorders/action', {
        data: {
          salesOrderItemId: log.salesOrderItemId,
          actionIdToRemove: log.actionId,
          statusField: log.statusField
        }
      });
      setLogs((prev) => prev.filter((entry) => entry.actionId !== log.actionId));
    } catch (error) {
      console.error("Error deleting data:", error);
      if (error.response?.data?.error) {
        alert(`Failed to delete data: ${error.response.data.error}`);
      } else {
        alert("Failed to update data. Please try again.");
      }
    }
  };

  if (!user) return <Error>⛔ Unauthorized</Error>;

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (error) return <Error>{error}</Error>;

  return (
    <Container>
      <ListContainer style={{ flex: '1 1 100%', maxWidth: '100%' }}>
        <Heading>Timeline Log</Heading>
        <FlexContainer
          direction="row"
          justify="flex-start"
          align="center"
          gap="20px"
          style={{
            marginBottom: '20px',
            padding: '10px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            flexWrap: 'wrap'
          }}>

          <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
            <label htmlFor="date">Date:</label>
            <Input type="date" name="date" value={filters.date} onChange={handleChange} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
            <label htmlFor="tech">Tech Name:</label>
            <Input type="text" name="tech" value={filters.tech} onChange={handleChange} placeholder="e.g. John" />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
            <label htmlFor="material">Material:</label>
            <Input type="text" name="material" value={filters.material} onChange={handleChange} placeholder="e.g. 41276" />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
            <label htmlFor="orderNumber">Order #:</label>
            <Input type="text" name="orderNumber" value={filters.orderNumber} onChange={handleChange} placeholder="e.g. 12345678" />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
            <label htmlFor="state">State:</label>
            <Select name="state" value={filters.state} onChange={handleChange}>
              <option value="">All</option>
              {["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"].map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </Select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
            <label htmlFor="statusType">Event:</label>
            <Select name="statusType" value={filters.statusType} onChange={handleChange}>
              <option value="">All</option>
              {["Received Status", "Collection Status", "Utilisation Status", "Fault Status", "Return Local Status", "Return Optus Status"].map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Select>
          </div>
        </FlexContainer>
        
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Tech</th>
                <th>State</th>
                <th>Event</th>
                <th>Qty</th>
                <th>Order #</th>
                <th>Material</th>
                <th>Comment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, i) => {
                const canDelete =
                  (user.role === 'tech' && log.techId === user.id) ||
                  (user.role === 'warehouseAdmin' && log.techId === user.id);

                return (
                  <TableRow key={i}>
                    <TableData data-label="Date">
                      {new Date(log.date).toLocaleString('en-AU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableData>
                    <TableData data-label="Tech">{log.techName}</TableData>
                    <TableData data-label="State">{log.state}</TableData>
                    <TableData data-label="Event">{log.statusField}</TableData>
                    <TableData data-label="Qty">{log.quantity}</TableData>
                    <TableData data-label="Order #">{log.orderNumber}</TableData>
                    <TableData data-label="Material">
                      <strong>{log.material}</strong> - {log.materialDesc}
                    </TableData>
                    <TableData data-label="Comment">{log.comment}</TableData>
                    <TableData data-label="Delete">
                      {canDelete && (
                        <button onClick={() => handleDelete(log)} style={{ color: 'red' }}>
                          🗑️
                        </button>
                      )}
                    </TableData>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </div>

      </ListContainer>
    </Container>
  );
};

export default Timeline;